import React, { useState } from 'react';
import { Box, TabScrollButton, styled,  } from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { Tab, TabPanel, Tabs, ActiveTabContext } from '../../common/components/tabs';
import { JobType, ShippingMode } from '../../common/apis/dtos';
import { Tracking } from '../tracking/Tracking';
import { ItemDetails } from '../itemDetails/ItemDetails';
import ContainerDetails from '../containerDetails/ContainerDetails';
import { TransportationLog } from '../transportationLog/TransportationLog';
import { useAdobeActions } from '../../common/hooks/useAnalyticsUIEvents';
import { ReferenceNumbers } from '../referenceNumbers/ReferenceNumbers';
import { JobWithAddedIds, TrackingEventWithId } from '@/common/internalModels';
import { OrderItemDetails } from '../orderDetails/OrderItemDetails';
import { getUniqueReferenceNumbersByType } from '@/common/utilities/referenceNumberUtils';

export interface ShipmentDetailsTabsProps {
  shipment: JobWithAddedIds;
  isMobileScreen: boolean;
}

const MyTabScrollButton = styled(TabScrollButton)({
  '&.Mui-disabled': {
    width: 0,
  },
  overflow: 'hidden',
  transition: 'width 0.5s',
  border: '1px solid #E5E6E7',
});

const ShipmentDetailsTabs = ({
  shipment,
  isMobileScreen,
}: ShipmentDetailsTabsProps) => {
  const { handleAdobeAction } = useAdobeActions();
  const translate = useTranslation();
  const [activeTab, setActiveTab] = useState('tracking');
  const showContainerDetails = shipment.mode === ShippingMode.Ocean && shipment.containers?.length > 0;
  const isNotOceanOrAir = shipment.mode !== ShippingMode.Ocean && shipment.mode !== ShippingMode.Air;
  const showItemDetails =  shipment.jobType !== JobType.Order && isNotOceanOrAir;
  const showOrderItemDetails = shipment.jobType === JobType.Order && isNotOceanOrAir;

  const handleChange = (
    e: React.SyntheticEvent<Element, Event>,
    value: string
  ) => {
    handleAdobeAction('tab', 'click', value);
    setActiveTab(value);
  };
  
  const pos = getUniqueReferenceNumbersByType(shipment.referenceNumbers??[], "PO");
  const bols = getUniqueReferenceNumbersByType(shipment.referenceNumbers??[], "BOL");
  const orderNumbers = getUniqueReferenceNumbersByType(shipment.referenceNumbers??[], "ORD");

  return (
    <ActiveTabContext.Provider value={activeTab}>
      <Box>
        <Tabs
          variant={isMobileScreen ? 'scrollable' : 'standard'}
          value={activeTab}
          onChange={handleChange}
          orientation="horizontal"
          allowScrollButtonsMobile
          aria-label={translate('Shipment Details')}
          ScrollButtonComponent={MyTabScrollButton}
        >
          <Tab
            label={`${translate('Tracking')}`}
            tabsId="shipmentDetails"
            value="tracking"
          />
          <Tab
            label={`${translate('Reference Numbers')}`}
            tabsId="shipmentDetails"
            value="referenceNumbers"
          />
          <Tab
            label={`${translate('Transportation Log')}`}
            tabsId="shipmentDetails"
            value="transportationLog"
          />
          {showContainerDetails && <Tab
            label={`${translate('Container Details')}`}
            tabsId="shipmentDetails"
            value="containerDetails"
          />}
          {showItemDetails && <Tab
            label={`${translate('Item Details')}`}
            tabsId="shipmentDetails"
            value="itemDetails"
          />}
          {showOrderItemDetails && <Tab
            label={`${translate('Item Details')}`}
            tabsId="orderDetails"
            value="orderitemDetails"
          />}
        </Tabs>
        <TabPanel tabsId="shipmentDetails" value="tracking">
          <Tracking shipment={shipment} isMobileScreen={isMobileScreen} />
        </TabPanel>
        <TabPanel tabsId="shipmentDetails" value="referenceNumbers">
          <ReferenceNumbers bolNumbers={bols} poNumbers={pos} orderNumbers={orderNumbers} isMobileScreen={isMobileScreen} />
        </TabPanel>
        <TabPanel tabsId="shipmentDetails" value="transportationLog">
          <TransportationLog trackingEvents={shipment.trackingEvents} isOcean={shipment.mode === ShippingMode.Ocean} />
        </TabPanel>
        {showContainerDetails && <TabPanel tabsId="shipmentDetails" value="containerDetails">
          <ContainerDetails shipment={shipment} isMobileScreen={isMobileScreen} />
        </TabPanel>}
        {showItemDetails && <TabPanel tabsId="shipmentDetails" value="itemDetails">
          <ItemDetails shipment={shipment} isMobileScreen={isMobileScreen} />
        </TabPanel>}
        {showOrderItemDetails && <TabPanel tabsId="orderDetails" value="orderitemDetails">
          <OrderItemDetails shipment={shipment} />
        </TabPanel>}
      </Box>
    </ActiveTabContext.Provider>
  );
};

export default ShipmentDetailsTabs;
